import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Home, ChevronDown, LogOut, User as UserIcon, Shield, Clock, Ticket, Plus, Sparkles, RefreshCw } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useCompany } from '../contexts/CompanyContext';
import type { SessionInfo } from '../api/session';
import { useAppVersion } from '../hooks/useAppVersion';
import { reloadToLatest } from '../lib/appVersion';
import ThemePicker from './ThemePicker';
import GlobalSearch from './GlobalSearch';
import WhatsNewModal from './WhatsNewModal';
import { WHATS_NEW } from '../lib/whatsNew';
import type { Company } from '../types';

const WHATS_NEW_DISMISSED_KEY = 'ctt-whats-new-dismissed';

const companyId = (c: Company & { id?: string }) => (c as any).id ?? (c as any).uuid;

interface Props {
  /** optional sub-label after the brand word-mark, e.g. "Portal" */
  subLabel?: string;
  /** hub route this header's Home button points at */
  homePath: string;
  /** where the user menu's Profile link goes */
  profilePath: string;
  /** quick-access route to the ticket listing (omit to hide the button) */
  ticketsPath?: string;
  /** quick-access route to create a ticket (omit to hide the button) */
  newTicketPath?: string;
  /** staff app only: What's New dialog — auto-pops once per unseen release,
      reopenable any time via the version badge or the user menu */
  showWhatsNew?: boolean;
}

/* ─────────────────────────────────────────────────────────────
   Always-on ctTicketing header. Owns ALL of: Home nav, company context
   chip, user menu, session timer, theme gear, logout. None of
   these may be duplicated inside a page body.
───────────────────────────────────────────────────────────── */
export default function ProjectHeader({ subLabel, homePath, profilePath, ticketsPath, newTicketPath, showWhatsNew }: Props) {
  const { user, logout } = useAuth();
  const { currentCompany, userCompanies, switchCompany } = useCompany();
  const navigate = useNavigate();

  const [companyOpen, setCompanyOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const companyRef = useRef<HTMLDivElement>(null);
  const userRef = useRef<HTMLDivElement>(null);

  // One session-info poll feeds the version badge, What's New, the update
  // prompt and the timer. The badge shows what this tab is RUNNING (the
  // build-time stamp), not what the server now serves — after a deploy those
  // differ, and UpdateAvailableBanner is what surfaces the gap.
  const { sessionInfo, buildVersion, serverVersion, updateAvailable } = useAppVersion();
  const version = buildVersion !== 'unknown' ? buildVersion : serverVersion;

  // What's New: reopenable on demand; auto-pops at most once per visit, and
  // only while the newest authored entry matches the running release and
  // hasn't been marked seen ("Got it").
  const whatsNewEnabled = !!showWhatsNew && WHATS_NEW.length > 0;
  const [whatsNewOpen, setWhatsNewOpen] = useState(false);
  const autoPopped = useRef(false);
  useEffect(() => {
    if (!whatsNewEnabled || autoPopped.current || !version) return;
    if (version !== WHATS_NEW[0].version) return;
    if (updateAvailable) return; // stale tab — refresh first, then read the notes
    let seen: string | null = null;
    try { seen = localStorage.getItem(WHATS_NEW_DISMISSED_KEY); } catch { /* private mode */ }
    if (seen === WHATS_NEW[0].version) return;
    autoPopped.current = true;
    setWhatsNewOpen(true);
  }, [whatsNewEnabled, version, updateAvailable]);

  const dismissWhatsNew = () => {
    try { localStorage.setItem(WHATS_NEW_DISMISSED_KEY, WHATS_NEW[0].version); } catch { /* private mode */ }
    setWhatsNewOpen(false);
  };

  // Close menus on outside-click / Escape
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!companyRef.current?.contains(e.target as Node)) setCompanyOpen(false);
      if (!userRef.current?.contains(e.target as Node)) setUserOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { setCompanyOpen(false); setUserOpen(false); } };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onClick); document.removeEventListener('keydown', onKey); };
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const canSwitch = userCompanies.length > 1;

  return (
    <header className="scpa-header">
      {/* LEFT cluster */}
      <div className="flex items-center gap-2 sm:gap-3 min-w-0">
        <div className="flex items-baseline gap-1.5 shrink-0">
          <Link to={homePath} style={{ textDecoration: 'none' }}>
            <span style={{ fontSize: 14, fontWeight: 800, letterSpacing: '-0.01em', color: 'var(--scpa-primary)' }}>
              ctTicketing
            </span>
          </Link>
          {/* A stale tab says so right in the header, so the one persistent
              affordance survives dismissing the banner with "Later". */}
          {updateAvailable ? (
            <button
              type="button"
              onClick={() => void reloadToLatest()}
              className="hidden sm:inline-flex items-center gap-1"
              style={{ fontSize: 11, fontWeight: 700, color: 'var(--scpa-status-error)', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
              title={`You're running v${buildVersion}; v${serverVersion} is available — click to refresh`}
            >
              <RefreshCw className="w-3 h-3" /> Update
            </button>
          ) : version && (whatsNewEnabled ? (
            <button
              type="button"
              onClick={() => setWhatsNewOpen(true)}
              className="hidden sm:inline"
              style={{ fontSize: 11, fontWeight: 600, color: 'var(--scpa-text-muted)', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
              title={`ctTicketing ${version} — see what's new`}
            >
              v{version}
            </button>
          ) : (
            <span
              className="hidden sm:inline"
              style={{ fontSize: 11, fontWeight: 600, color: 'var(--scpa-text-muted)' }}
              title={`ctTicketing version ${version}`}
            >
              v{version}
            </span>
          ))}
          {subLabel && (
            <>
              <span className="hidden sm:inline" style={{ color: 'var(--scpa-text-muted)' }}>·</span>
              <span className="hidden sm:inline" style={{ fontSize: 14, fontWeight: 600, color: 'var(--scpa-text-muted)' }}>
                {subLabel}
              </span>
            </>
          )}
        </div>

        {/* Hidden on phones — the brand word-mark links home already */}
        <Link to={homePath} className="scpa-ghost-btn hidden sm:inline-flex" title="Home">
          <Home className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Home</span>
        </Link>

        {ticketsPath && <GlobalSearch />}
        {ticketsPath && (
          <Link to={ticketsPath} className="scpa-ghost-btn" title="Ticket listing">
            <Ticket className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Tickets</span>
          </Link>
        )}

        {newTicketPath && (
          <Link
            to={newTicketPath}
            className="scpa-ghost-btn"
            title="Create a new ticket"
            style={{ color: 'var(--scpa-primary)', fontWeight: 600 }}
          >
            <Plus className="w-3.5 h-3.5" /> <span className="hidden sm:inline">New ticket</span>
          </Link>
        )}

        {user?.isSuperAdmin && (
          <Link to="/admin" className="scpa-ghost-btn" title="Admin">
            <Shield className="w-3.5 h-3.5" /> <span className="hidden md:inline">Admin</span>
          </Link>
        )}
      </div>

      {/* RIGHT cluster — ml-auto keeps it right-aligned when the header wraps */}
      <div className="flex items-center gap-2 ml-auto">
        {/* Context chip — company switcher */}
        <div ref={companyRef} className="relative">
          <button
            type="button"
            className="scpa-context-chip"
            onClick={() => canSwitch && setCompanyOpen(v => !v)}
            title={canSwitch ? 'Switch company' : currentCompany?.name}
            style={{ cursor: canSwitch ? 'pointer' : 'default' }}
          >
            <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: 'var(--scpa-status-ok)' }} />
            <span className="truncate max-w-[4.5rem] sm:max-w-[10rem]">{currentCompany?.name ?? 'No company'}</span>
            {canSwitch && <ChevronDown className="w-3 h-3 shrink-0" />}
          </button>
          {companyOpen && canSwitch && (
            <div className="scpa-menu right-0 top-full mt-1.5 max-h-72 overflow-y-auto" role="menu">
              {userCompanies.map(c => (
                <button
                  key={companyId(c)}
                  type="button"
                  role="menuitem"
                  className="scpa-menu-item"
                  onClick={() => { switchCompany(companyId(c)); setCompanyOpen(false); navigate(homePath); }}
                  style={companyId(currentCompany!) === companyId(c) ? { background: 'var(--scpa-primary-muted)', fontWeight: 600 } : undefined}
                >
                  {c.name}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* User menu */}
        <div ref={userRef} className="relative">
          <button
            type="button"
            className="scpa-ghost-btn"
            onClick={() => setUserOpen(v => !v)}
            style={{ color: 'var(--scpa-primary)' }}
            aria-haspopup="menu"
            aria-expanded={userOpen}
          >
            <UserIcon className="w-3.5 h-3.5 shrink-0" />
            <span className="truncate max-w-[8rem] hidden sm:inline">{user?.name ?? user?.email ?? 'Account'}</span>
            <ChevronDown className="w-3 h-3" />
          </button>
          {userOpen && (
            <div className="scpa-menu right-0 top-full mt-1.5" role="menu">
              <Link to={profilePath} role="menuitem" className="scpa-menu-item" onClick={() => setUserOpen(false)}>
                <UserIcon className="w-4 h-4" /> Profile
              </Link>
              {whatsNewEnabled && (
                <button
                  type="button"
                  role="menuitem"
                  className="scpa-menu-item"
                  onClick={() => { setUserOpen(false); setWhatsNewOpen(true); }}
                >
                  <Sparkles className="w-4 h-4" /> What&apos;s new
                </button>
              )}
              <button type="button" role="menuitem" className="scpa-menu-item" onClick={handleLogout}>
                <LogOut className="w-4 h-4" /> Sign out
              </button>
            </div>
          )}
        </div>

        <SessionTimer info={sessionInfo} />
        <ThemePicker />

        {/* Hidden on phones — Sign out lives in the user menu */}
        <button type="button" className="scpa-ghost-btn hidden sm:inline-flex" onClick={handleLogout} title="Sign out" aria-label="Sign out">
          <LogOut className="w-3.5 h-3.5" />
        </button>
      </div>

      {whatsNewEnabled && (
        <WhatsNewModal
          open={whatsNewOpen}
          onClose={() => setWhatsNewOpen(false)}
          onDismiss={dismissWhatsNew}
          currentVersion={version}
        />
      )}
    </header>
  );
}

/* Live session-lifetime indicator, rendered from server values
   (clock-skew corrected). The poll lives in ProjectHeader; this ticks locally. */
function SessionTimer({ info: data }: { info?: SessionInfo }) {
  const [, force] = useState(0);

  useEffect(() => {
    const t = setInterval(() => force(n => n + 1), 1000);
    return () => clearInterval(t);
  }, []);

  if (!data?.authenticated || !data.expiresAt) return null;

  const offset = data.now - Date.now();           // server − client at fetch
  const remaining = data.expiresAt - (Date.now() + offset);
  if (remaining <= 0) return null;

  const label = formatRemaining(remaining);
  return (
    // Hidden below md — an icon-only timer conveys nothing and costs header space
    <span className="scpa-ghost-btn hidden md:inline-flex" style={{ cursor: 'default' }} title="Time left in this session">
      <Clock className="w-3.5 h-3.5" /> {label}
    </span>
  );
}

function formatRemaining(ms: number): string {
  const s = Math.floor(ms / 1000);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}
